/* HOYA accessibility for the cart drawer and the cart page (WCAG 2.2 AA, audit findings CART-04, -05, -06, -14).
   Prestige moves focus into the drawer when it opens (its `initial-focus`, sections/cart-drawer.liquid) and back to the
   opener when it closes. This file adds what the theme cannot do:
   - CART-06: while the drawer is open, the page behind it is inert, so a screen reader cannot wander behind it. Focus
     still outside the drawer at that moment goes to its title, the same target as `initial-focus` (CART-02).
   - CART-04: the drawer rebuilds its content on every cart change, which drops keyboard focus to the page. Focus goes
     back to the same control of the same line, else to the drawer title.
   - CART-05: one polite status line in the drawer says what changed ("נוסף לסל: …" from the product page, quick buy or
     a suggestion; the new quantity; a removal), then the new total.
   - CART-14: the cart page reloads after a quantity change or a suggestion added. Focus comes back to the same control
     (or the page title when the line is gone) and the page's status line says what changed.
   Texts come from locales (hoya_a11y_cart.*) through data attributes. Nothing here changes the cart or the flow. */
(() => {
  if (window.__hoyaA11yCart) return;
  window.__hoyaA11yCart = true;

  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template || '');
  const lineKey = line => {
    const input = line.querySelector('[data-line-key]');
    if (input) return input.dataset.lineKey;
    const change = line.querySelector('a[href*="/cart/change"]');
    return change ? new URL(change.href, window.location.href).searchParams.get('id') || '' : '';
  };
  const lineName = line => (line.querySelector('.line-item-info a[href*="/products/"]') || line.querySelector('.line-item-info p'))?.textContent.trim() || '';
  // Which of a line's controls had focus: the quantity "−", the field, the "+", or the remove link.
  const controlOf = element => {
    const selector = element.closest('quantity-selector');
    if (element.matches('[data-line-key]')) return 'input';
    if (selector) return element === selector.querySelector('.quantity-selector__button') ? 'minus' : 'plus';
    return element.matches('line-item-quantity a') ? 'remove' : '';
  };
  const controlIn = (scope, key, control) => {
    const input = [...scope.querySelectorAll('[data-line-key]')].find(el => el.dataset.lineKey === key && el.getClientRects().length);
    if (!input) return null;
    const buttons = input.closest('quantity-selector').querySelectorAll('.quantity-selector__button');
    const target = { input, minus: buttons[0], plus: buttons[1], remove: input.closest('line-item-quantity')?.querySelector('a.link') }[control];
    return target && !target.disabled && target.getClientRects().length ? target : input;
  };

  /* Cart drawer ------------------------------------------------------------------------------------------------- */
  const drawer = document.getElementById('cart-drawer');
  if (drawer) {
    const title = () => drawer.querySelector('#cart-drawer-title');
    const isOpen = () => drawer.hasAttribute('open');
    let shown = false;
    let queued = '';

    // The drawer's own children are replaced on every render; the status line lives in its shadow content instead.
    const statusLine = () => {
      const root = drawer.shadowRoot;
      if (!root) return null;
      let line = root.querySelector('[data-hoya-cart-status]');
      if (!line) {
        line = document.createElement('p');
        line.setAttribute('role', 'status');
        line.dataset.hoyaCartStatus = '';
        line.style.cssText = 'position:absolute;inline-size:1px;block-size:1px;margin:-1px;padding:0;overflow:hidden;clip-path:inset(50%);white-space:nowrap;border:0';
        (root.querySelector('[part="content"]') || root).append(line);
      }
      return line;
    };
    const say = message => {
      const line = statusLine();
      if (!line) return;
      line.textContent = '';
      setTimeout(() => { line.textContent = message; }, 150);
    };
    statusLine();

    drawer.addEventListener('dialafter-show', () => {
      // A drawer closed again during its opening animation stays closed: nothing to do.
      if (!isOpen()) return;
      shown = true;
      [...document.body.children].forEach(element => {
        if (element === drawer || element.contains(drawer) || element.hasAttribute('inert') || element.matches('script, style, link, template')) return;
        element.setAttribute('inert', '');
        element.dataset.hoyaCartInert = '';
      });
      // Focus never stays on the page that is now inert. The theme's first focus moves to the same title right after
      // this (`initial-focus`), so both agree; this also covers a theme version that gives touch screens no first focus.
      if (!drawer.contains(document.activeElement)) title()?.focus({ preventScroll: true });
      // An add from the product page opens the drawer: its title is read first, then what was added.
      if (queued) {
        const message = queued;
        queued = '';
        setTimeout(() => say(message), 400);
      }
    });
    // Before Prestige returns focus to the opener (after the closing animation).
    drawer.addEventListener('dialbefore-hide', () => {
      shown = false;
      queued = '';
      document.querySelectorAll('[data-hoya-cart-inert]').forEach(element => {
        element.removeAttribute('inert');
        delete element.dataset.hoyaCartInert;
      });
    });

    // Prestige's quantity change sends line-item:change from the <line-item>, and its product form (product page, quick
    // buy) sends variant:add with the added items, both right before cart:change.
    let changedLine = null;
    let addedItems = null;
    document.addEventListener('line-item:change', event => {
      const line = event.target.closest?.('line-item');
      changedLine = line ? { name: lineName(line), quantity: Number(event.detail?.quantity) || 0 } : null;
    }, true);
    document.addEventListener('variant:add', event => { addedItems = event.detail?.items || null; }, true);

    // cart:change is sent on <html> and the drawer listens on document, so this runs before the rebuild.
    let pending = null;
    document.documentElement.addEventListener('cart:change', event => {
      const detail = event.detail || {};
      const active = document.activeElement;
      const inside = isOpen() && drawer.contains(active) && active !== drawer;
      const line = inside ? active.closest('line-item') : null;
      const added = detail.baseEvent === 'variant:add';
      pending = {
        added,
        names: added ? (detail.items || addedItems || []).map(item => item.product_title || item.title).filter(Boolean) : [],
        line: added ? null : changedLine,
        focus: inside ? { key: line ? lineKey(line) : '', control: line ? controlOf(active) : '' } : null,
        empty: detail.cart?.item_count === 0
      };
      changedLine = null;
      addedItems = null;
    });

    drawer.addEventListener('cart-drawer:refreshed', () => {
      const was = pending;
      pending = null;
      if (!was || !isOpen()) return;
      // CART-04: focus that was in the drawer comes back to the same control, else to the title.
      if (was.focus) (was.focus.key && controlIn(drawer, was.focus.key, was.focus.control) || title())?.focus({ preventScroll: true });
      // CART-05
      const texts = drawer.dataset;
      let message = '';
      if (was.empty) message = texts.statusEmpty || '';
      else if (was.added && was.names.length) message = fill(texts.statusAdded, { title: was.names.join(', ') });
      else if (was.line && was.line.quantity > 0) message = fill(texts.statusQuantity, { title: was.line.name, quantity: String(was.line.quantity) });
      else if (was.line) message = fill(texts.statusRemoved, { title: was.line.name });
      if (!message) return;
      const total = drawer.querySelector('.cart-drawer__button-price')?.textContent.trim();
      if (total && !was.empty) message = `${message} ${fill(texts.statusTotal, { total })}`;
      if (shown) say(message);
      else queued = message;
    });
  }

  /* Cart page --------------------------------------------------------------------------------------------------- */
  const status = document.querySelector('[data-cart-status]');
  const form = status?.closest('form');
  if (form) {
    const KEY = 'hoya:cart-return:v1';
    const keep = data => {
      try {
        sessionStorage.setItem(KEY, JSON.stringify({ ...data, at: Date.now() }));
      } catch (_) {}
    };
    const rowName = element => {
      const line = element.closest('tr')?.querySelector('line-item');
      return line ? lineName(line) : '';
    };
    form.addEventListener('click', event => {
      const change = event.target.closest?.('line-item-quantity a[href*="/cart/change"]');
      const add = event.target.closest?.('[data-hu-add]');
      if (change) keep({ key: new URL(change.href, window.location.href).searchParams.get('id') || '', control: controlOf(change), name: rowName(change) });
      else if (add) keep({ added: add.closest('.hu-item')?.querySelector('.hu-item__title')?.textContent.trim() || '' });
    }, true);
    form.addEventListener('change', event => {
      if (event.target.matches?.('[data-line-key]')) keep({ key: event.target.dataset.lineKey, control: 'input', name: rowName(event.target) });
    }, true);

    let back = null;
    try {
      back = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      sessionStorage.removeItem(KEY);
    } catch (_) {}
    if (back && Date.now() - back.at < 20000) {
      const target = back.key ? controlIn(form, back.key, back.control) : null;
      if (target) target.focus();
      else {
        const heading = document.querySelector('.shopify-section--main-cart h1');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus();
      }
      const input = target ? target.closest('quantity-selector')?.querySelector('[data-line-key]') || [...form.querySelectorAll('[data-line-key]')].find(el => el.dataset.lineKey === back.key) : null;
      const texts = status.dataset;
      const message = back.added ? fill(texts.statusAdded, { title: back.added })
        : input ? fill(texts.statusQuantity, { title: back.name, quantity: input.value })
        : fill(texts.statusRemoved, { title: back.name });
      setTimeout(() => { status.textContent = message; }, 600);
    }
  }
})();
